import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}

const HomeItems: Record<string, string[]> = {
    "bedroom.png": ["bed.png", "wardrobe.png", "pillow.png", "alarm_clock.png", "blanket.png", "mirror.png"],
    "bathroom.png": ["bathtub.png", "toilet.png", "shower.png", "towel.png", "toothbrush.png", "soap.png", "shampoo.png", "hairdryer.png"],
    "livingroom.png": ["sofa.png", "television.png", "lamp.png", "bookshelf.png", "clock.png", "painting.png", "coffee_table.png", "cushion.png"],
    "kitchen.png": ["stove.png", "refrigerator.png", "microwave.png", "sink.png", "toaster.png", "cabinet.png", "knife.png", "spoon.png", "plate.png"]
};


const MatchHomeItems: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const [showImage, setShowImage] = useState(false);


    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch questions dynamically
    const fetchQuestions = () => {
        const usedKeys: string[] = [];
        const homeKeys = Object.keys(HomeItems);
        const shuffledKeys = shuffleArray([...homeKeys]);
        const selectedKeys = shuffledKeys.slice(0, 4); console.log(selectedKeys)
        const pairs = selectedKeys.map((key, index) => {
            const images = HomeItems[key];
            const randomImage = images[Math.floor(Math.random() * images.length)];
            usedKeys.push(randomImage);
        });
        const newQuestions: ImagePair[] = [];

        const suffledNumbers = shuffleArray([...usedKeys]);
        selectedKeys.map((key, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: usedKeys.indexOf(suffledNumbers[index]) + 1,
                leftImage: key,
                rightImage: suffledNumbers[index],
            });
        })

        setQuestions(newQuestions);
    };

    useEffect(() => {
        resetConnections();
        fetchQuestions();
        document.title = "Match the Home Items – Nursery Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        resetConnections();
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 2000);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Support early learning with the "Match Home Items" activity on Mamas Guidance—an engaging exercise for nursery kids to match household items with their corresponding rooms, boosting observation skills and general awareness.' />
            </Helmet>
            {!showImage ? (
                <>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Match the Home Items</h1>

                            <p>
                                Help your child understand the items in their home with this engaging <strong>Match the Home Items</strong> worksheet! Designed for nursery-level learners, this activity encourages children to <strong>connect household objects to the correct rooms</strong> — all while having fun.
                            </p>

                            <h2>🪑 What’s in the Worksheet?</h2>
                            <p>
                                The worksheet displays common household objects such as <b><em>bed, sofa, stove, toothbrush, and dining table</em></b>. Children will match each item to its corresponding room: <strong>bedroom, living room, kitchen, bathroom, and dining area</strong>.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize and name basic household items</li>
                                <li>Associate objects with their appropriate rooms</li>
                                <li>Develop <strong>logical thinking</strong> and <strong>classification skills</strong></li>
                                <li>Enhance <strong>vocabulary</strong> and <strong>general awareness</strong></li>
                            </ul>

                            <h2>👩‍🏫 How to Use This Worksheet</h2>
                            <ul>
                                <li>Ask your child to identify each household item.</li>
                                <li>Read the room options aloud or let them explore visually.</li>
                                <li>Draw lines to match each object to its room.</li>
                                <li>Discuss where and how the object is used in real life.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/general-awareness/festivals/guess-festival"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Guess Festivals</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Pick Color</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Guess Community Helper</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Important</h2>
                            <p>
                                Helping children understand their environment <strong>builds confidence</strong> and <strong>independence</strong>. This worksheet fosters <strong>spatial awareness, item recognition</strong>, and everyday language skills essential for young learners.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Match the Home Items</h1>

                        <p>
                            Help your child understand the items in their home with this engaging <strong>Match the Home Items</strong> worksheet! Designed for nursery-level learners, this activity encourages children to <strong>connect household objects to the correct rooms</strong> — all while having fun.
                        </p>

                        <h2>🪑 What’s in the Worksheet?</h2>
                        <p>
                            The worksheet displays common household objects such as <b><em>bed, sofa, stove, toothbrush, and dining table</em></b>. Children will match each item to its corresponding room: <strong>bedroom, living room, kitchen, bathroom, and dining area</strong>.
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Recognize and name basic household items</li>
                            <li>Associate objects with their appropriate rooms</li>
                            <li>Develop logical thinking and classification skills</li>
                            <li>Enhance vocabulary and general awareness</li>
                        </ul>

                        <h2>👩‍🏫 How to Use This Worksheet</h2>
                        <ul>
                            <li>Ask your child to identify each household item.</li>
                            <li>Read the room options aloud or let them explore visually.</li>
                            <li>Draw lines to match each object to its room.</li>
                            <li>Discuss where and how the object is used in real life.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/general-awareness/festivals/guess-festival"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess Festivals</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Pick Color</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Guess Community Helper</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🧠 Why This Activity Is Important</h2>
                        <p>
                            Helping children understand their environment builds confidence and independence. This worksheet fosters <strong>spatial awareness, item recognition</strong>, and everyday language skills essential for young learners.
                        </p>
                    </article>
                    <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", height: "calc(-200px + 100vh)", marginTop: "0%" }}>{/* SVG for drawing lines */}
                        <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                            {connections.map((conn, index) => (
                                <line
                                    key={index}
                                    x1={conn.x1}
                                    y1={conn.y1}
                                    x2={conn.x2}
                                    y2={conn.y2}
                                    stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                    strokeWidth="3"
                                />
                            ))}
                        </svg>
                        <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%", height: "100%" }}>
                            <div className="left-column">
                                {questions.map((item) => (
                                    <div
                                        key={item.left_id}
                                        className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                        onClick={(e) => handleLeftClick(e, item.left_id)}
                                        style={{ cursor: "pointer", padding: "10px" }}
                                    >
                                        <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + item.leftImage} alt="Left Option" style={{ width: "100px", height: "100px" }} />
                                    </div>
                                ))}
                            </div>
                            <div className="right-column">
                                {questions.map((item) => (
                                    <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                        <img className="img-thumbnail" src={process.env.REACT_APP_API_URL + item.rightImage} alt="Right Option" style={{ width: "100px", height: "100px" }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="btn hero-btn" onClick={nextQuestion}>Next Question</div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchHomeItems;
